import {Text} from "@/components/ui/text";
import Footer from "@/components/basic/Footer";
import {ScrollView, View} from "react-native";
import {VStack} from "@/components/ui/vstack";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import i18n from "i18n-js";
import Moment from "moment/moment";
import OnlineButton from "@/components/basic/OnlineButton";
import {backendRequest} from "@/stores/thunks";
import ImageSynced from "@/components/media/ImageSynced";
import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {Divider} from "@/components/ui/divider";
import {useSafeAreaInsets} from "react-native-safe-area-context";
import {HStack} from "@/components/ui/hstack";

export default function PlannerRequests({navigation}){

    const contacts = useSelector(s => s.contacts.active, shallowEqual);

    const {requestsFrom, requestsTo} = useMemo(() => ({
        /** @type {Contact[]} */ requestsFrom: contacts.filter(c => c.planningRequestFrom !== null),
        /** @type {Contact[]} */ requestsTo: contacts.filter(c => c.planningRequestTo !== null)
    }), [contacts]);

    const fromCount = requestsFrom.length;
    const toCount = requestsTo.length;

    const online = useSelector((state) => state.server.online)
    const insets = useSafeAreaInsets()
    const paddingBottom = (online ? 140 : 90) + insets.bottom;
    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(backendRequest('GET_CONTACTS_INLINE', '/contact/user', null, 'GET', false))
    }, [dispatch])

    const actions = [
        { icon: 'keyboard-arrow-left', text: 'footer.back', component: 'BACK' },
        { icon: 'home', text: 'footer.home', component: 'Home' },
        { icon: 'send', text: 'plannerRequest.send', component: 'RequestPlanning'}
    ]

    return <View className="flex-1 bg-primary-50">
            <ScrollView className="flex-1 bg-secondary-50 py-3 px-3" scrollIndicatorInsets={{ right: 1 }} contentContainerStyle={{ paddingBottom }}>
                <VStack space='2xl'>
                    <VStack space='xl'>
                        <Text className="text-2xl font-normal text-secondary-900">{i18n.t('plannerRequest.plannerRequests')}</Text>
                        <Text className="text-xl font-normal px-3 text-secondary-900" >{i18n.t('plannerRequest.requestFrom', { num: fromCount || 0 })}</Text>
                        {requestsFrom.map((contact) => <PlannerRequestListElement key={contact.id} contact={contact} sender={false} />)}
                        <Divider />
                        <Text className="text-xl font-normal px-3 leading-8 text-secondary-900" >{i18n.t('plannerRequest.requestTo', { num: toCount || 0 })}</Text>
                        {requestsTo.map((contact) => <PlannerRequestListElement key={contact.id} contact={contact} sender={true} />)}
                    </VStack>
                </VStack>
            </ScrollView>
            <Footer navigation={navigation} actions={actions}/>
        </View>
}

/**
 * @param {Contact} contact
 * @param {boolean} sender
 */
const PlannerRequestListElement = React.memo(function PlannerRequestListElement({contact, sender}) {
    const dispatch = useDispatch()
    /**@type{ImageLoad}*/ const imgLoad = useSelector((state) => state.images.load[contact.toUser.id])

    const handleAccept = useCallback(
        () => dispatch(backendRequest('ACCEPT_PLANNER_REQUEST', `/contact/planner/${contact.id}/accept`, null, 'PUT')), [dispatch, contact.id]
    )

    const handleCancel = useCallback(
        () => dispatch(backendRequest('CANCEL_PLANNER_REQUEST', `/contact/planner/${contact.id}/cancel`, null, 'PUT')), [dispatch, contact.id]
    )

    const handleReject = useCallback(
        () => dispatch(backendRequest('REJECT_PLANNER_REQUEST', `/contact/planner/${contact.id}/reject`, null, 'PUT')), [dispatch, contact.id]
    )

    return (
        <View>
            {sender ? (
                <VStack space='md'>
                    <HStack space='sm'>
                        <ImageSynced
                            key={contact.toUser.id + '_' + imgLoad?.duration}
                            uri={contact.toUser.id}
                            sync={'/user/' + contact.toUser.id + '/userImage'}
                            e ksize='sm' />
                        <VStack space='sm' className="mt-3">
                            <Text className="text-xl font-bold leading-7 text-secondary-900">{contact.toUser.mail}</Text>
                            <Text className="text-lg font-normal leading-6 text-secondary-900">{i18n.t('contactManagement.request', { date: Moment(contact.planningRequestTo).format('DD.MM.YYYY') })}</Text>
                        </VStack>
                    </HStack>
                    <HStack className="justify-between" space='xs'>
                        <OnlineButton onPress={handleCancel}>{i18n.t('contactManagement.cancel')}</OnlineButton>
                    </HStack>
                </VStack>
            ) : (
                <VStack space='md'>
                    <HStack space='sm'>
                        <ImageSynced
                            key={contact.toUser.id + '_' + imgLoad?.duration}
                            uri={contact.toUser.id}
                            sync={'/user/' + contact.toUser.id + '/userImage'}
                            size='sm' />
                        <VStack space='sm' className="mt-3">
                            <Text bold className="text-xl text-secondary-900">{contact.toUser.mail}</Text>
                            <Text className="text-lg text-secondary-900">{i18n.t('contactManagement.request', { date: Moment(contact.planningRequestFrom).format('DD.MM.YYYY') })}</Text>
                        </VStack>
                    </HStack>
                    <HStack justifyContent='space-between' space='xs'>
                        <OnlineButton onPress={handleAccept}>{i18n.t('contactManagement.accept')}</OnlineButton>
                        <OnlineButton onPress={handleReject} variant='outline' >{i18n.t('contactManagement.deny')}</OnlineButton>
                    </HStack>
                </VStack>
            )}
        </View>
    )
})